import { useSenderGroupingStore } from './senderGroupingStore';
import { useTriageStore } from './triageStore';
import { useEmailStore } from './emailStore';

/**
 * Whether a mailbox reload must be DEFERRED (queued as `pendingMailboxReload`)
 * rather than run now, because a non-mailbox source currently owns the visible
 * list. Shared by BOTH ends of the reload queue:
 *
 *  - the `mail:mailbox:reload` handler (enqueue side), and
 *  - `flushPendingMailboxReload` (drain side).
 *
 * The drain side must re-check because state can change between enqueue and
 * drain — e.g. bootstrap flushes on every `emailsLoading` falling edge, and a
 * sender-group load-more flips that flag, so a reload queued while in a group
 * would otherwise fire straight into the active group (the L1 clobber the
 * routing was built to prevent).
 */
export function shouldDeferMailboxReload(): boolean {
  const senderState = useSenderGroupingStore.getState();
  const viewingSenderGroup = senderState.groupBySender && Boolean(senderState.activeSenderGroup);
  const digestVisible = senderState.senderDigestVisible;

  // Defer if there are pending group actions (archiving/deleting sender groups)
  // to prevent scroll jumps and race conditions during bulk operations.
  const hasPendingGroupActions = Object.keys(senderState.pendingGroupActions).length > 0;

  // Defer while triage owns the (invisible) list — it runs ONE reconciliation
  // reload on close (R12).
  const inTriage = useTriageStore.getState().isTriageMode;

  // A briefing item drill-down owns the visible list exactly like a sender
  // group does — a mailbox reload over it is the same L1 clobber (stale scope
  // header atop mailbox rows). Ownership is read from the email store's
  // listSource, the single source of truth for who owns the list.
  const briefingItemOwnsList = useEmailStore.getState().listSource.kind === 'briefingItem';

  return viewingSenderGroup || digestVisible || hasPendingGroupActions || inTriage || briefingItemOwnsList;
}
